import * as React from 'react';
import { Button, Field, Icon, PaginatedTable } from '@teamsnap/teamsnap-ui';
import { Status } from '@teamsnap/snap-ui';
import { Organization } from 'models';
import * as _ from 'lodash';
import { Placement } from '@teamsnap/teamsnap-ui/dist/js/types/placement';
import * as ListPaymentAccounts from 'core/gql/queries/list-payment-accounts';
import { useLazyQuery, useMutation } from '@apollo/client';

import CreateAccountDrawer from 'components/form/create-account-drawer';
import './organization-accounts-management.scss';
import PaymentAccount from 'models/payment-account';
import * as CreateOnboardingLink from 'core/gql/mutations/create-onboarding-link';
import { ORG_DEPOSIT_ACCOUNTS_URL } from 'core/utils';

interface Props {
  organization: Organization;
}

export enum AccountStatus {
  APPROVED = 'approved',
  UNDER_REVIEW = 'under_review',
  INFO_NEEDED = 'information_required',
  BANK_DETAILS_REQUIRED = 'bank_details_required',
  RESTRICTED_SOON = 'restricted_soon',
  REJECTED = 'rejected',
}

const columns = [
  { name: 'account', label: 'Account', isSortable: false, mods: 'u-size4of12' },
  { name: 'status', label: 'Status', isSortable: false, mods: 'u-size4of12' },
  { name: 'accountId', label: 'Account ID', isSortable: false, mods: 'u-size4of12' },
  { name: 'actions', label: '', isSortable: false, mods: 'u-size4of12' },
];

const OrgAccountsManagement: React.FunctionComponent<Props> = ({ organization }) => {
  const [showDrawer, setShowDrawer] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [paymentAccounts, setPaymentAccounts] = React.useState([]);
  const [editBankAccount, setEditBankAccount] = React.useState(null);

  const searchInputRef = React.useRef(null);

  const [getPaymenetAccounts, serverResponse] = useLazyQuery(ListPaymentAccounts.QUERY);

  const [createOnboardingLink, createOnboardingLinkResponse] = useMutation(CreateOnboardingLink.MUTATION);

  const { data, loading } = serverResponse;

  const handleVerifyAccount = async (item: PaymentAccount) => {
    const variables = {
      id: item.id,
      returnUrl: ORG_DEPOSIT_ACCOUNTS_URL(organization.id),
      refreshUrl: ORG_DEPOSIT_ACCOUNTS_URL(organization.id),
      isMerchant: true,
      type: 'account_update'
    };

    const response = await createOnboardingLink({ variables });

    if (response) {
      const url = response.data?.createOnboardingLink?.url;

      if (url) {
        window.location.href = url;
      }
    }
  };

  const redirectUrl = ORG_DEPOSIT_ACCOUNTS_URL(organization.id);

  const handleDrawerClose = () => {
    document.body.classList.remove('scroll-lock');
    setShowDrawer(false);
    setEditBankAccount(null);
  };

  const handleDrawerOpen = () => {
    setShowDrawer(true);
  };

  const search = (event) => {
    event.preventDefault();

    if (searchTerm) {
      const searchResults = data.listPaymentAccounts?.filter(
        (account: PaymentAccount) =>
          account.nickname?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          account.bankAccountNumberLast4?.includes(searchTerm)
      );

      setPaymentAccounts(searchResults);
    } else {
      setPaymentAccounts(data.listPaymentAccounts);
    }
  };

  const isApproved = (item: PaymentAccount) => {
    if (item) return item.status === AccountStatus.APPROVED || item.status === AccountStatus.RESTRICTED_SOON;
    return false;
  };

  const renderEditButton = (item: PaymentAccount) =>
    isApproved(item) ? (
      <Button
        type="link"
        onClick={() => {
          setShowDrawer(true);
          setEditBankAccount(item);
        }}
        testId="OrganizationAccountsManagement--edit-button"
      >
        Edit
      </Button>
    ) : null;

  const loadData = async ({ page, itemsPerPage, sortBy, sortAsc }) => {
    const startIndex = itemsPerPage * page - itemsPerPage;

    let finalData = _.sortBy(paymentAccounts, sortBy);
    if (sortAsc) {
      finalData = _.reverse(finalData);
    }

    const endIndex = Math.min(finalData.length, startIndex + itemsPerPage);
    return { data: finalData.slice(startIndex, endIndex), totalItems: finalData.length };
  };

  const accountOptionStatus = (account: PaymentAccount) => (
    <>
      {account?.status === AccountStatus.APPROVED && (
        <Status testId="OrganizationAccountManagement--account-option-status" state="success" text="Approved" />
      )}
      {account?.status === AccountStatus.RESTRICTED_SOON && (
        <>
          <Status
            testId="OrganizationAccountManagement--account-option-status"
            state="warning"
            text="Restricted soon"
          />
          {verifyAccountButton(account)}
        </>
      )}
      {account?.status === AccountStatus.REJECTED && (
        <Status testId="OrganizationAccountManagement--account-option-status" state="negative" text="Rejected" />
      )}
      {account?.status === AccountStatus.UNDER_REVIEW && (
        <Status testId="OrganizationAccountManagement--account-option-status" state="negative" text="Under review" />
      )}
      {account?.status === AccountStatus.INFO_NEEDED && (
        <>
          <Status
            testId="OrganizationAccountManagement--account-option-status"
            state="negative"
            text={'Information required'}
          />
          {verifyAccountButton(account)}
        </>
      )}
      {account?.status === AccountStatus.BANK_DETAILS_REQUIRED && (
        <>
          <Status
            testId="OrganizationAccountManagement--account-option-status"
            state="negative"
            text={'Bank details required'}
          />
          {verifyAccountButton(account)}
        </>
      )}
    </>
  );

  const verifyAccountButton = (account: PaymentAccount) => (
    <>
      <div>
        <Button
          isDisabled={createOnboardingLinkResponse.loading}
          type="link"
          onClick={() => handleVerifyAccount(account)}
          testId="OrganizationAccountsManagement--verify-account-button"
        >
          Verify account
        </Button>
      </div>
    </>
  );

  const mapData = (item: PaymentAccount) => ({
    id: item.id,
    account: (
      <div>
        <div className="merchant-account__nickname">{item.nickname}</div>
        {item.bankAccountNumberLast4 && <div className="merchant-account__details">*{item.bankAccountNumberLast4}</div>}
      </div>
    ),
    status: accountOptionStatus(item),
    accountId: item.externalAccountId,
    actions: <div className="merchant-account__row-actions">{renderEditButton(item)}</div>,
  });

  React.useEffect(() => {
    getPaymenetAccounts({
      variables: { scopeId: +organization.id, scopeType: 'organization' },
    });
  }, [getPaymenetAccounts, organization.id]);

  React.useEffect(() => {
    if (data && data.listPaymentAccounts) {
      setPaymentAccounts(data.listPaymentAccounts);
    }
  }, [data]);

  return (
    <div className="u-padTopXs">
      <div id="merchant-accounts-management__header" className="u-flex u-flexJustifyEnd">
        <form onSubmit={search} className="u-flex u-flexGrow1 u-flexAlignItemsCenter u-posRelative u-padRightSm">
          <Icon name="search" mods="search-icon u-colorNeutral6" />
          <Field
            type="input"
            mods="search-input u-sizeFull"
            style={{ flex: 1 }}
            name="searchTerm"
            formFieldProps={{
              placeholder: 'Search by account name or last four digits of the merchant account',
              inputProps: {
                value: searchTerm,
                onChange: (e) => setSearchTerm(e.target.value),
                maxLength: 255,
                ref: searchInputRef,
              },
            }}
          />
          <Button type="submit" mods="u-spaceSidesSm">
            Search
          </Button>
        </form>
        <Button
          color="primary"
          icon="plus"
          iconPosition="left"
          testId="OrganizationAccountsManagement--create-account-button"
          onClick={handleDrawerOpen}
        >
          Create Account
        </Button>
      </div>

      <div id="merchant-accounts-management">
        <PaginatedTable
          columns={columns}
          isLoading={loading}
          paginationPlacement={Placement.Bottom}
          mapDataToRow={mapData}
          reloadDependency={paymentAccounts}
          loadData={loadData}
          searchPlaceholder="Search members by account name or last four digits of the merchant account"
          defaultItemsPerPage={10}
          hideRowsSelect={true}
        />
      </div>

      <CreateAccountDrawer
        setAccount={setEditBankAccount}
        account={editBankAccount}
        organization={organization}
        isDrawOpen={showDrawer}
        onDrawClose={handleDrawerClose}
        redirectUrl={redirectUrl}
      />
    </div>
  );
};

export default OrgAccountsManagement;
